import Vue from 'vue';
import {login, recoverPassword, resetPassword, getLoggedUser, changePassword, logout, switchOrganization} from '@/api/auth';

const auth = {
  state: {
    token: null,
    emailSent: false,
    passwordReset: false,
    passwordExpired: false,
    userInfo: null,
    userOrganizationId: null,
    userOrganizationRoles: null,
    organizationIds: [],
    currentOrganizationId: null,
    role: null,
    mainRoleName: null,
    isViewingAsAdmin: false,
  },
  getters: {
    getRole: state => state.role,
    getMainRoleName: state => state.mainRoleName,
    isViewingAsAdmin: state => state.isViewingAsAdmin,
    getEmailSentState: state => state.emailSent,
    getResetPasswordState: state => state.passwordReset,
    getPasswordExpiredState: state => state.passwordExpired,
    getOrganizationId: state => state.currentOrganizationId || state.userOrganizationId,
    getOrganizationRoles: state => state.userOrganizationRoles,
    getOrganizationIds: state => state.organizationIds,
    getCurrentOrganizationId: state => state.currentOrganizationId,
    isMultiOrg: state => state.organizationIds.length > 1,
    me: state => state.userInfo
  },
  mutations: {
    SET_USER(state, user) {
      state.userInfo = user;
    },
    SET_ROLE(state, role) {
      state.role = role;
    },
    SET_MAIN_ROLE_NAME(state, name) {
      state.mainRoleName = name;
    },
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_EMAIL_SENT(state, value) {
      state.emailSent = value;
    },
    SET_RESET_PASSWORD(state, value) {
      state.passwordReset = value;
    },
    SET_USER_ORGANIZATION_ID(state, value) {
      state.userOrganizationId = value;
    },
    SET_USER_ORGANIZATIONS_ROLES(state, value) {
      state.userOrganizationRoles = value;
    },
    SET_PASSWORD_EXPIRED(state, value) {
      state.passwordExpired = value;
    },
    SET_ORGANIZATION_IDS(state, value) {
      state.organizationIds = value || [];
    },
    SET_CURRENT_ORGANIZATION_ID(state, value) {
      state.currentOrganizationId = value;
      if (value) {
        localStorage.setItem('currentOrganizationId', value);
      } else {
        localStorage.removeItem('currentOrganizationId');
      }
    },
    SET_VIEW_AS_ADMIN(state, value) {
      state.isViewingAsAdmin = value;
    }
  },
  actions: {
    // Point d'entrée unique d'un nouveau jeton : connexion ici, ou arrivée depuis `app` par ?access_token= (routeur).
    SetToken(context, token) {
      Vue.ls.set('Authorization', token);
      context.commit('SET_TOKEN', token);

      // Decode current_organization_id from JWT payload (base64url → base64 pour atob)
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.current_organization_id) {
          context.commit('SET_CURRENT_ORGANIZATION_ID', payload.current_organization_id);
        }
      } catch (e) {
        // JWT decode failed — will be resolved by GetLoggedUser
      }
    },
    Login(context, user) {
      return new Promise((resolve, reject) => {
        login(user).then(response => {
          context.dispatch('SetToken', response.access_token);

          // Store organization context from login response
          if (response.organization_ids) {
            context.commit('SET_ORGANIZATION_IDS', response.organization_ids);
          }

          resolve();
        }).catch(error => {
          reject(error)
        })
      })
    },
    Logout() {
      return new Promise((resolve, reject) => {
        logout().then(() => {
          Vue.ls.remove('Authorization');
          localStorage.removeItem('currentOrganizationId');
          window.location = '/user/login';
          resolve()
        }).catch(error => {
          Vue.ls.remove('Authorization');
          localStorage.removeItem('currentOrganizationId');
          window.location = '/user/login';
          reject(error);
        })
      })
    },
    GetLoggedUser: (context) => {
      return new Promise((resolve, reject) => {
        getLoggedUser().then(response => {
          const result = response.data;
          context.commit('SET_USER', result);
          context.commit('SET_ROLE', result.main_role.id);
          context.commit('SET_MAIN_ROLE_NAME', result.main_role.name);
          context.commit('SET_PASSWORD_EXPIRED', result.password_expired || false);
          context.commit('SET_USER_ORGANIZATION_ID', result.organization ? result.organization.id : null);
          context.commit('SET_USER_ORGANIZATIONS_ROLES', result.user_organization_role);

          // Restore currentOrganizationId from localStorage if not set
          if (!context.state.currentOrganizationId) {
            const savedOrgId = localStorage.getItem('currentOrganizationId');
            if (savedOrgId) {
              context.commit('SET_CURRENT_ORGANIZATION_ID', parseInt(savedOrgId));
            }
          }

          resolve(response);
        }).catch(error => {
          console.log('error');
          reject(error);
        })
      })
    },
    SwitchOrganization(context, organizationId) {
      return new Promise((resolve, reject) => {
        switchOrganization(organizationId).then(response => {
          const token = response.access_token;
          Vue.ls.set('Authorization', token);
          context.commit('SET_TOKEN', token);
          context.commit('SET_CURRENT_ORGANIZATION_ID', organizationId);

          if (response.organization_ids) {
            context.commit('SET_ORGANIZATION_IDS', response.organization_ids);
          }

          resolve(response);
        }).catch(error => {
          reject(error);
        })
      })
    },
    RecoverPassword(context, data) {
      return new Promise((resolve, reject) => {
        recoverPassword(data).then(response => {
          resolve(response);
        }).catch(error => {
          reject(error);
        })
      })
    },
    ReturnToLogin(context) {
      context.dispatch('Logout')
      context.commit('SET_EMAIL_SENT', false)
      context.commit('SET_RESET_PASSWORD', false)
    },
    ResetPassword(context, params) {
      return new Promise((resolve, reject) => {
        resetPassword(params).then(response => {
          resolve(response);
        }).catch(error => {
          reject(error);
        })
      })
    },
    ChangePassword(context, params) {
      return new Promise((resolve, reject) => {
        changePassword(params).then(() => {
          resolve();
        }).catch(error => {
          reject(error)
        })
      })
    }
  },
}

export default auth
